import { useNavigate } from '@tanstack/react-router'
import { HugeiconsIcon } from '@hugeicons/react'
import { Tick01Icon } from '@hugeicons/core-free-icons'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { ConfirmRemoveUserStep } from './ConfirmRemoveUserStep'
import { ReassignDirectReportsStep } from './ReassignDirectReportsStep'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  useAuthState,
  useMyConvexOrganizationId,
  usePermissions,
} from '@/features/auth'
import {
  useDirectReportsForManager,
  useOrganizationManagers,
  useOrganizationUsers,
  useConvexOrganizationRoles,
} from '@/features/organization/services/organization.convex'
import { useMutation } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import type { OrganizationUser } from '@/features/organization/types'

type Step = 'reassign' | 'confirm' | 'success'

interface RemoveUserDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  userId: string
  userName: string
  onSuccess?: () => void
}

export function RemoveUserDialog({
  open,
  onOpenChange,
  userId,
  userName,
  onSuccess,
}: RemoveUserDialogProps) {
  const navigate = useNavigate()
  const { currentOrganization } = useAuthState()
  const { hasOrganizationRole } = usePermissions()
  const kindeOrgId = currentOrganization?.organizationId
  const convexOrgId = useMyConvexOrganizationId()
  const isAdmin = kindeOrgId ? hasOrganizationRole(kindeOrgId, 'admin') : false

  // Convex mutation callables
  const updateUserRolesFn = useMutation(api.organization.updateUserRoles)
  const changeUserManagerFn = useMutation(api.organization.changeUserManager)
  const removeUserFn = useMutation(api.organization.removeUser)

  // State
  const [step, setStep] = useState<Step>('reassign')
  const [isLoading, setIsLoading] = useState(false)
  const [assignments, setAssignments] = useState<Record<string, string | null>>(
    {},
  )
  const [grantManagerIds, setGrantManagerIds] = useState<Array<string>>([])

  // Fetch direct reports for the user being removed
  const { data: directReportsRaw, isLoading: isLoadingDirectReports } =
    useDirectReportsForManager(userId, convexOrgId)
  const directReports = directReportsRaw as unknown as OrganizationUser[]

  // Fetch organization managers list
  const { data: managersRaw = [] } = useOrganizationManagers(convexOrgId)
  const managerIds = useMemo(
    () => new Set(managersRaw.map((m) => m.id)),
    [managersRaw],
  )

  // Fetch candidate users for reassignment
  const { data: allCandidatesRaw = [], isLoading: candidatesLoading } =
    useOrganizationUsers(convexOrgId)
  const allCandidates = allCandidatesRaw as unknown as OrganizationUser[]

  const { data: orgRoles = [] } = useConvexOrganizationRoles()

  // Determine if we should skip reassign step
  const hasDirectReports = directReports.length > 0

  function setAssignment(drId: string, managerId: string | null) {
    setAssignments((s) => ({ ...s, [drId]: managerId }))
  }

  function handleClose() {
    // Reset state
    setStep('reassign')
    setAssignments({})
    setGrantManagerIds([])
    onOpenChange(false)
  }

  // Get planned items for reassignment
  const plannedItems = useMemo(() => {
    return Object.entries(assignments)
      .filter(([, v]) => !!v)
      .map(([drId, candidateId]) => {
        const candidate = allCandidates.find((c) => c.id === candidateId)
        const dr = directReports.find((d) => d.id === drId)
        return {
          userId: drId,
          userName: dr?.name,
          candidateId: candidateId as string,
          candidateName: candidate?.name,
          isManager: managerIds.has(candidateId as string),
        }
      })
  }, [assignments, allCandidates, directReports, managerIds])

  // Count assignments
  const assignedCount = Object.values(assignments).filter(Boolean).length
  const allAssigned = assignedCount === directReports.length

  // Check if all planned can be executed
  const executableCount = plannedItems.filter(
    (it) =>
      it.isManager || (isAdmin && grantManagerIds.includes(it.candidateId)),
  ).length

  // Handle proceed from reassign step
  function handleProceedToConfirm() {
    if (!hasDirectReports) {
      setStep('confirm')
      return
    }

    if (!allAssigned) {
      toast.error('Please assign all direct reports before proceeding')
      return
    }

    if (executableCount === 0) {
      toast.error(
        'No reassignments can be executed. Please select managers or grant manager rights.',
      )
      return
    }

    setStep('confirm')
  }

  // Handle final removal
  async function handleRemoveUser(reason: string) {
    if (!convexOrgId) {
      toast.error('Organization not found')
      return
    }

    setIsLoading(true)

    try {
      // If there are direct reports, execute reassignments first
      if (hasDirectReports && plannedItems.length > 0) {
        // Grant manager roles if needed
        if (grantManagerIds.length > 0) {
          const managerRole = orgRoles.find(
            (r) => String(r.name).toLowerCase() === 'manager',
          )
          const roleId = managerRole?.id
          if (roleId) {
            for (const candidateId of grantManagerIds) {
              await updateUserRolesFn({
                userId: candidateId as Id<'users'>,
                organizationId: convexOrgId as Id<'organizations'>,
                roleIds: [roleId as Id<'organizationRoles'>],
              })
            }
          }
        }

        // Execute reassignments
        const results: Array<{ userId: string; ok: boolean; error?: string }> =
          []
        for (const item of plannedItems) {
          // Skip non-manager assignments if not admin
          if (!item.isManager && !isAdmin) continue
          // Skip if not granting manager rights
          if (!item.isManager && !grantManagerIds.includes(item.candidateId))
            continue

          try {
            await changeUserManagerFn({
              employeeUserId: item.userId as Id<'users'>,
              newManagerUserId: item.candidateId as Id<'users'>,
              organizationId: convexOrgId as Id<'organizations'>,
            })
            results.push({ userId: item.userId, ok: true })
          } catch (err: unknown) {
            results.push({
              userId: item.userId,
              ok: false,
              error: err instanceof Error ? err.message : 'error',
            })
          }
        }

        const failed = results.filter((r) => !r.ok)
        if (failed.length > 0) {
          toast.error('Some reassignments failed. User will not be removed.')
          setIsLoading(false)
          return
        }
      }

      // Delete the user
      await removeUserFn({
        userId: userId as Id<'users'>,
        organizationId: convexOrgId as Id<'organizations'>,
        reason: reason || 'No reason provided',
      })

      setStep('success')
      toast.success(`${userName} has been removed from the organization`)

      // Call success callback after a short delay
      setTimeout(() => {
        handleClose()
        onSuccess?.()
        navigate({ to: '/direct-reports' })
      }, 1500)
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to remove user'
      toast.error(message)
    } finally {
      setIsLoading(false)
    }
  }

  // Loading state
  if (isLoadingDirectReports) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-150">
          <DialogHeader>
            <DialogTitle>Remove User</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-8">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Success step
  if (step === 'success') {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-100">
          <DialogHeader>
            <DialogTitle>User Removed</DialogTitle>
            <DialogDescription>
              {userName} has been removed from the organization.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <div className="h-12 w-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
              <HugeiconsIcon
                icon={Tick01Icon}
                className="h-6 w-6 text-primary"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  // Confirm step
  if (step === 'confirm' || !hasDirectReports) {
    return (
      <ConfirmRemoveUserStep
        open={open}
        userName={userName}
        hasDirectReports={hasDirectReports}
        isLoading={isLoading}
        onConfirm={handleRemoveUser}
        onBack={() => setStep('reassign')}
        onCancel={handleClose}
      />
    )
  }

  // Reassign step (only shown if user has direct reports)
  return (
    <ReassignDirectReportsStep
      open={open}
      userName={userName}
      userId={userId}
      directReports={directReports}
      allCandidates={allCandidates}
      managerIds={managerIds}
      isAdmin={isAdmin}
      isLoading={isLoading}
      assignments={assignments}
      onAssignmentChange={setAssignment}
      grantManagerIds={grantManagerIds}
      onGrantManagerIdsChange={setGrantManagerIds}
      assignedCount={assignedCount}
      allAssigned={allAssigned}
      executableCount={executableCount}
      hasNextPage={false}
      isFetchingNextPage={candidatesLoading}
      fetchNextPage={() => {}}
      onProceed={handleProceedToConfirm}
      onCancel={handleClose}
    />
  )
}
